import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaTasks,
  FaSignOutAlt,
  FaUserCircle,
  FaChevronDown,
  FaChevronUp,
  FaGavel,
  FaFileAlt,
  FaFile,
  FaFileContract,
  FaTruck,
  FaChartBar,
  FaBars,
  FaRegFileAlt,
  FaShieldAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClipboardList,
} from "react-icons/fa";
import { FiBox } from "react-icons/fi";
import { MdDashboard } from "react-icons/md";

const HOSidebar = ({ visible, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [collapsedMenus, setCollapsedMenus] = useState({});
  const [collapsed, setCollapsed] = useState(false);
  const userInteractions = useRef({});
  const isInitialMount = useRef(true);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser({
          firstName: parsed.firstName || parsed.name?.split(" ")[0] || "User",
          lastName: parsed.lastName || parsed.name?.split(" ")[1] || "",
          email: parsed.email,
          profilePic: parsed.profilePic,
        });
      } catch (err) {
        console.error("Error parsing user from localStorage", err);
        localStorage.removeItem("user");
      }
    }

    if (isInitialMount.current) {
      const initialExpandedState = {};
      menuItems.forEach((item) => {
        if (item.children) {
          const isChildActive = item.children.some(
            (child) => location.pathname === child.path
          );
          if (isChildActive) {
            initialExpandedState[item.label] = true;
          }
        }
      });
      setCollapsedMenus(initialExpandedState);
      isInitialMount.current = false;
    }
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/");
  };

  const toggleMenu = (label) => {
    userInteractions.current[label] = true;
    
    setCollapsedMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  const shouldMenuBeOpen = (item) => {
    if (!item.children) return false;
    
    if (userInteractions.current[item.label] !== undefined) {
      return collapsedMenus[item.label] || false;
    }
    
    const isChildActive = item.children.some(
      (child) => location.pathname === child.path
    );
    
    return isChildActive;
  };

  const menuItems = [
    {
      label: "Dashboard",
      icon: <MdDashboard size={18} />,
      path: "/head-of-operations-dashboard",
    },
    {
      label: "Project compliance Review",
      icon: <FaClipboardList size={16} />,
      path: "/project-compliance",
    },
    
    {
      label: "Risk Register",
      icon: <FaGavel size={16} />,
      path: "/risk-register",
    },
    {
      label: "Procurement Plan Reviews",
      icon: <FaTasks size={16} />,
      path: "/procurement-plans",
    },
    {
      label: "Vendor Management",
      icon: <FaFileContract size={16} />,
      
      children: [
        { label: "Vendor Profiles", icon: <FaUserCircle size={14} />, path: "/vendor-managementHO" },
        { label: "Vendor Contracts", icon: <FaFileContract size={14} />, path: "/vendor-contractsHO" },
        { label: "Vendor Invoices", icon: <FaFileContract size={14} />, path: "/vendor-invoicesHO" },
      ],
    },
    {
      label: "Equipment Maintenance", 
      icon: <FiBox size={16} />, 
      path: "/equipment-maintenance"
    },
    {
      label: "Reports",
      icon: <FaChartBar size={16} />,
      path: "/hoc-reports",
    },
    { label: "Disciplinary", icon: <FaGavel size={16} />, path: "/hoc-discipline" },
    { label: "Timesheet", icon: <FaFileAlt size={16} />, path: "/head-of-operations/timesheet" },
    { label: "Documentation", icon: <FaFile size={16} />, path: "/hoc-documentation" },
    { label: "My Profile", icon: <FaUserCircle size={16} />, path: "/hoc-settings" },
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Top Section: Logo + Collapse Toggle */}
      <div className="flex items-center justify-between px-4 py-6">
        {!collapsed && <img src="/logo.jpeg" alt="Logo" className="w-28" />}
        <button
          onClick={toggleSidebar}
          className="text-gray-600 hover:text-orange-500"
          title={collapsed ? "Expand" : "Collapse"}
        >
          <FaBars />
        </button>
      </div>

      {/* Middle Section: Scrollable Menu */}
      <div
        className="flex-1 overflow-y-auto px-2"
        style={{
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <style>
          {`
            div::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
        <ul className="space-y-1">
          {menuItems.map((item, index) => {
            const hasChildren = item.children && item.children.length > 0;
            const isActive = item.path && location.pathname === item.path;
            const isChildActive =
              hasChildren &&
              item.children.some((child) => location.pathname === child.path);
            const isOpen = shouldMenuBeOpen(item);

            return (
              <li key={index}>
                {item.path && hasChildren ? (
                  <div
                    className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer
                      ${isActive
                        ? "bg-orange-100 text-orange-600 font-semibold"
                        : "text-gray-700 hover:bg-gray-100 hover:text-orange-500 transition-all duration-200"
                      }
                    `}
                  >
                    <Link to={item.path} className="flex items-center gap-3 flex-1">
                      {item.icon}
                      {!collapsed && <span>{item.label}</span>}
                    </Link>
                    {!collapsed && (
                      <button onClick={() => toggleMenu(item.label)} className="ml-2">
                        {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                    )}
                  </div>
                ) : item.path ? (
                  <Link to={item.path}>
                    <div
                      className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer
                        ${isActive
                          ? "bg-orange-100 text-orange-600 font-semibold"
                          : "text-gray-700 hover:bg-gray-100 hover:text-orange-500 transition-all duration-200"
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        {item.icon}
                        {!collapsed && <span>{item.label}</span>}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div
                    className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer
                      ${isActive
                        ? "bg-orange-100 text-orange-600 font-semibold"
                        : "text-gray-700 hover:bg-gray-100 hover:text-orange-500 transition-all duration-200"
                      }
                    `}
                    onClick={() => hasChildren ? toggleMenu(item.label) : null}
                  >
                    <div className="flex items-center gap-3">
                      {item.icon}
                      {!collapsed && <span>{item.label}</span>}
                    </div>
                    {hasChildren && !collapsed && (
                      <span>{isOpen ? <FaChevronUp /> : <FaChevronDown />}</span>
                    )}
                  </div>
                )}

                {hasChildren && isOpen && !collapsed && (
                  <ul className="ml-6 mt-1 space-y-1">
                    {item.children.map((child, cIndex) => {
                      const isChildActive =
                        child.path && location.pathname === child.path;
                      return (
                        <li key={cIndex}>
                          <Link to={child.path}>
                            <div
                              className={`flex items-center gap-2 px-3 py-1 rounded-lg cursor-pointer
                                ${isChildActive
                                  ? "bg-orange-50 text-orange-600 font-medium"
                                  : "text-gray-600 hover:bg-gray-100 hover:text-orange-500"
                                }
                              `}
                            >
                              {child.icon}
                              <span>{child.label}</span>
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* Bottom Section: User Info + Logout */}
      <div className="flex-shrink-0 px-4 py-4 border-t">
        {user ? (
          <div className="flex items-center gap-3 mb-4">
            {user.profilePic ? (
              <img
                src={user.profilePic}
                alt="Profile"
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <FaUserCircle className="w-10 h-10 text-gray-400" />
            )}
            {!collapsed && (
              <div>
                <p className="text-sm font-semibold">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="animate-pulse flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            {!collapsed && (
              <div>
                <div className="w-24 h-4 bg-gray-200 rounded mb-1"></div>
                <div className="w-32 h-4 bg-gray-200 rounded"></div>
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleLogout}
          className="flex items-center w-full gap-3 px-3 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-all duration-200"
        >
          <FaSignOutAlt />
          {!collapsed && "Logout"}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden md:flex flex-col fixed left-0 top-0 w-64 h-screen bg-white shadow-md z-50">
        <SidebarContent />
      </div>

      {/* Mobile Sidebar */}
      {visible && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          ></div>
          <div className="relative bg-white w-64 h-full shadow-md z-50">
            <SidebarContent />
          </div>
        </div>
      )}
    </>
  );
};

export default HOSidebar;